import { isDev } from './env';
import isAbsolutePath from './isAbsolutePath';
import isAnchorPath from './isAnchorPath';

export interface InternalLinkProps {
  link?: string;
  pathPrefix?: string;
  siteDomain: string;
  blocklist?: string[];
}

const DEFAULT_BLOCKLIST = ['documents', 'users?', 'blog'];

const getInternalLink = ({ link = '', pathPrefix, siteDomain, blocklist }: InternalLinkProps) => {
  const currentSiteDomain = isDev() ? 'localhost:8000' : siteDomain;
  const blocklists = blocklist && !!blocklist.length ? [...DEFAULT_BLOCKLIST, ...blocklist] : DEFAULT_BLOCKLIST;
  const blocklistRegex = blocklists ? `${blocklists.join('|')}` : '';

  const stripPathPrefix = (path: string) => {
    if (pathPrefix && pathPrefix === path) {
      return path;
    }

    if (pathPrefix && pathPrefix !== '/') {
      return path.replace(pathPrefix, '');
    }

    return path;
  };

  const siteDomainRegexValue = `^(?:https?:)?(?://)?(?:www.)?(?:${currentSiteDomain})(.*)`;
  const blocklistPathRegexValue = `^(?!(?:(?:/|/${pathPrefix}/)?(?:[a-zA-Z]{2}-)?(?:[a-zA-Z]{2})?)?(?:/)?(?:${blocklistRegex}))(.*)`;
  const siteDomainRegex = new RegExp(siteDomainRegexValue, 'gi');
  const isSiteDomainRegex = new RegExp(siteDomainRegexValue, 'gi');
  // trim leading and trailing whitespace
  const currentLink = link.trim();
  const isSameDomain = isSiteDomainRegex.test(currentLink);

  if (isSameDomain && isAbsolutePath(currentLink)) {
    const siteDomainValue = siteDomainRegex.exec(currentLink);
    const blocklistPathRegex = new RegExp(blocklistPathRegexValue, 'gi');
    const sameDomainPath = (siteDomainValue && siteDomainValue[1]) || '';
    const isPathNotBlocked = sameDomainPath && blocklistPathRegex.test(sameDomainPath);
    return isPathNotBlocked
      ? { isInternal: true, url: stripPathPrefix(sameDomainPath) }
      : { isInternal: false, url: currentLink };
  }

  const isLocalLink = (path: string) => path.startsWith('/');
  if (isAbsolutePath(currentLink)) {
    return {
      isInternal: false,
      url: currentLink,
    };
  }

  if (isLocalLink(currentLink)) {
    const blocklistLocalLinkRegex = new RegExp(blocklistPathRegexValue, 'gi');
    const isLocalLinkPathNotBlocked = blocklistLocalLinkRegex.test(currentLink);
    return isLocalLinkPathNotBlocked
      ? {
          isInternal: true,
          url: stripPathPrefix(currentLink),
        }
      : {
          isInternal: false,
          url: currentLink,
        };
  }

  if (currentLink && !isAnchorPath(currentLink) && !currentLink.startsWith('?')) {
    return {
      isInternal: false,
      url: `//${currentLink}`,
    };
  }

  return {
    isInternal: false,
    url: currentLink,
  };
};

export default getInternalLink;
